import { rem } from 'polished'
import type { ParsedQs } from 'qs'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { InView } from 'react-intersection-observer'
import { useIntl } from 'react-intl'

import LineArrowDownIcon from 'LuxKit/Icons/line/LineArrowDownIcon'
import type { AccommodationRoomTileActionsState } from 'components/AccommodationKit/AccommodationRoomTileActions'
import AccommodationRoomTileActions from 'components/AccommodationKit/AccommodationRoomTileActions'
import AccommodationSameRoomTileImage from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomTileImage'
import { Experiments } from 'constants/experiments'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import { shouldShowRoomsRemainingLabelForVariant } from 'components/Common/Labels/urgencyLabelVisibility'
import FormatCurrency from 'components/Common/FormatCurrency'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Pane from 'components/Common/Pane'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import OfferPageFocusAnchor from 'components/OfferPage/OfferPageFocusAnchor'
import { toOfferPageSnapshotId } from 'tripPlanner/assistant/surfaces/offerPage/offerPageSnapshotText'
import type { OfferPageFilterType, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useExperiment from 'hooks/Experiments/useExperiment'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { useQsQueryParams } from 'hooks/useQueryParams'
import { take } from 'lib/array/arrayUtils'
import { hasSelectedRooms, preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import { stringParam } from 'lib/url/queryStringUtils'
import { isGhaRequest } from 'lib/url/ghaRequestUtils'
import uuidV4 from 'lib/string/uuidV4Utils'
import {
  arePropertyRatesLoading,
  getPropertyOfferMapping,
  getPropertyRatesKey,
  isPropertyAvailableForPurchase,
} from 'selectors/offerPage/offerPageSelectors'

import PropertyRateOption from './PropertyRateOption'
import PropertyRoomAvailabilityStrip from './PropertyRoomAvailabilityStrip'
import useGroupedPropertyRatesByRefundability from './useGroupedPropertyRatesByRefundability'
import PropertyOfferPageLoyaltyStayUpgradeCallOutBanner from './PropertyOfferPageLoyaltyStayUpgradeCallOutBanner'
import {
  getPropertyRateUpgradeCandidate,
  type PropertyRateUpgradeCandidate,
} from 'components/OfferPage/offerPageUtils/propertyLoyaltyPackageUpgrade'
import AccommodationSameRoomTile from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomTile'
import AccommodationSameRoomTileDetails from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomTileDetails'
import { AccommodationSameRoomTileOptions } from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomTileOptions'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import PropertyAnytimeRateOption from './PropertyAnytimeRateOption'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import { INITIAL_PROPERTY_ROOM_RATES_TO_SHOW, MAX_PROPERTY_ROOM_RATES_TO_SHOW } from './propertyRoomConstants'
import PropertyRoomDesktopPackages from './PropertyRoomDesktopPackages'
import { getPropertyRateDisplayPrice } from './propertyCollapsedRoomPackageUtils'
import useLogMetasearchInterestedOption from 'hooks/OfferPage/useLogMetasearchInterestedOption'

/**
 * `property.singleUnit` content isn't reliably set for every whole-property rental, so also
 * treat properties with exactly one room type as single-unit (matches the non-UNIQUE_STAYS
 * fallback gating in `PropertyUniqueStaysRecommendations`). `datesAvailable` isn't meaningful
 * while rates for the current selection are still loading — without the `isLoading` guard, every
 * date change would momentarily read as sold-out and fire off the strip's own alternative-dates
 * fetches before we actually know the selected dates are unavailable.
 */
export function shouldShowRoomAvailabilityStrip(
  property: App.Property.Property,
  roomTypeCount: number,
  checkIn: string | undefined,
  checkOut: string | undefined,
  datesAvailable: boolean,
  isLoading: boolean,
): boolean {
  const isSingleUnitProperty = !!property.singleUnit || roomTypeCount === 1
  return isSingleUnitProperty && !!checkIn && !!checkOut && !isLoading && !datesAvailable
}

export function getInitialRatesToShow(
  showOnlyInterestedRate: boolean,
): number {
  return showOnlyInterestedRate ? 1 : INITIAL_PROPERTY_ROOM_RATES_TO_SHOW
}

interface Props {
  property: App.Property.Property
  offer: App.HotelOffer | undefined
  /** The room-rate-view roomId (matches the assistant snapshot's `package:<roomId>` target). */
  roomId: string
  propertyRoom: App.Property.Room
  availableRooms: number | undefined
  rates: Array<App.EnrichedPropertyRate>
  allRatesQuickFiltered: boolean
  roomFitsCapacity: boolean
  datesAvailable: boolean
  checkIn?: string
  checkOut?: string
  hasLpcMapping: boolean
  rooms: Array<OfferPageRoom>
  isSelected: boolean
  isHighlighted?: boolean
  roomRateUnmatched?: boolean
  onViewPackageOptions: () => void
  roomListId: string
  roomPosition: number
  availableForPurchase?: boolean
  areAnytimeRates: boolean
  isLoading: boolean
  duration: number
  /** Number of selectable room types on the property, used to detect single-unit-style properties that don't set `property.singleUnit`. */
  roomTypeCount: number
}

function PropertyRoomOption(props: Props) {
  const {
    property,
    offer,
    roomId,
    propertyRoom,
    availableRooms,
    rates,
    allRatesQuickFiltered,
    roomFitsCapacity,
    datesAvailable,
    checkIn,
    checkOut,
    hasLpcMapping,
    rooms,
    isSelected,
    isHighlighted = false,
    roomRateUnmatched = true,
    onViewPackageOptions,
    roomListId,
    roomPosition,
    availableForPurchase,
    areAnytimeRates,
    isLoading,
    duration,
    roomTypeCount,
  } = props

  const intl = useIntl()
  const query = useQsQueryParams<ParsedQs>()
  const isGhaTraffic = isGhaRequest(query)
  const isNonMobileDevice = !useIsMobileDevice()
  const firstRate = rates[0]
  const expectedRateId = stringParam(query.roomRateId) || stringParam(query.roomOptionRateId)

  /************************************************************************************************/
  /* STOP! Do not add any more hooks for deriving packages/rooms or options/rates to this         */
  /* component! This logic is being refactored out into selectors! If you _need_ to add new       */
  /* logic, speak to Xiao.                                                                        */
  /************************************************************************************************/

  // NB: `0`s become `undefined` so we can easily conditionally render `<BookingsCountLabel/>` later.
  const bookingsCount = propertyRoom.stats.booked || undefined
  const hasAvailableRooms = typeof availableRooms === 'number'
  const canShowRatePriceRoomsRemainingLabel =
    !!checkIn &&
    !!firstRate &&
    shouldShowRoomsRemainingLabelForVariant({
      roomCount: hasAvailableRooms ? availableRooms : 0,
      hasRatePrice: true,
      threshold: 'rate',
      copyVariant: 'rate-price',
    })
  const [roomUrgencyInView, setRoomUrgencyInView] = useState(false)
  const { isAnyVariant: shouldShowSppRoomUrgency } = useExperiment(Experiments.croUrgencyOnSppRooms, {
    canActivate: roomUrgencyInView && canShowRatePriceRoomsRemainingLabel,
  })
  const offerPageDispatch = useOfferPageDispatch()
  const promoteRoom = isHighlighted || isSelected
  const promoteRoomWithSpecificRate = !isSelected && isHighlighted && !roomRateUnmatched
  const promoteRoomWithSomeRates = (isSelected || (isHighlighted && roomRateUnmatched)) && !!rates.length
  const promoteRoomWithoutRates = (isSelected || (isHighlighted && roomRateUnmatched)) && !rates.length
  const { isAnyVariant: showMultipleRates } = useExperiment(Experiments.ghaSppOnlyShowInterestedRate, {
    canActivate: promoteRoomWithSpecificRate && isGhaTraffic && isNonMobileDevice,
  })
  useLogMetasearchInterestedOption({
    enabled: isHighlighted && !isLoading,
    found: !roomRateUnmatched && !!firstRate,
    price: firstRate?.totals.displayPrice,
    rateId: firstRate?.service.rateId ?? firstRate?.rateId,
    expectedRateId,
    propertyId: property.id,
    checkIn,
    checkOut,
    rooms,
    missingReason: rates.length ? 'rate-not-matched-in-highlighted-room' : 'highlighted-room-has-no-rates',
  })
  const showOnlyInterestedRate = promoteRoomWithSpecificRate && !(isGhaTraffic && isNonMobileDevice && showMultipleRates)
  const initialRatesToShow = getInitialRatesToShow(showOnlyInterestedRate)
  const roomCount = rooms.length

  useEffect(() => {
    if (isHighlighted) {
      onViewPackageOptions()
    }
  }, [isHighlighted]) // oxlint-disable-line react-hooks/exhaustive-deps

  // Alternative dates rely on the LPC inventories-date-range endpoint; LPP-only properties
  // do not expose that inventory lookup, so we only offer the CTA when an LPC mapping exists.
  //
  // We also disable this when there's an active dynamic Flash campaign promotion, as these use a
  // Flash-like calendar (where cells for days we care about either have prices or are marked as
  // "sold out") that effectively conveys availability.
  const shouldShowAlternativeDatesCta = !!checkIn && !!checkOut && !datesAvailable && hasLpcMapping && property.promotion?.type !== 'dynamicFlash'

  const showRoomAvailabilityStrip = shouldShowRoomAvailabilityStrip(property, roomTypeCount, checkIn, checkOut, datesAvailable, isLoading)

  const actionsState = useMemo<AccommodationRoomTileActionsState | undefined>(() => {
    if (isLoading) return undefined
    if (!availableForPurchase) return 'sold-out'

    if (availableForPurchase) {
      if (allRatesQuickFiltered) return 'available-if-quick-filters-relaxed'
      const anytimeRatesActionState = areAnytimeRates ? 'available-on-dates-variant' : 'available-on-dates'
      if (!checkIn) return anytimeRatesActionState
      if (!roomFitsCapacity && datesAvailable) return 'exceeded-capacity'
      if (!datesAvailable) return 'sold-out-on-dates-or-exceeded-capacity'
    }
  }, [allRatesQuickFiltered, areAnytimeRates, availableForPurchase, checkIn, datesAvailable, isLoading, roomFitsCapacity])

  const toggleFilter = useCallback((filter: OfferPageFilterType) => {
    offerPageDispatch({ type: OfferPageActions.setPropertyRoomId, roomId: propertyRoom.id })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter })
  }, [offerPageDispatch, propertyRoom.id])

  const handleChangeDatesClick = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.setAlternativeDatesFlow, isAlternativeDatesFlow: shouldShowAlternativeDatesCta })
    toggleFilter('checkin')
  }, [offerPageDispatch, shouldShowAlternativeDatesCta, toggleFilter])

  const handleCheckAvailabilityClick = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.setAlternativeDatesFlow, isAlternativeDatesFlow: false })
    // If dates aren't set, the CTA will be "Select dates", so we'll use the default occupancy and
    // skip directly to selecting dates.
    toggleFilter((areAnytimeRates || hasSelectedRooms(rooms)) ? 'checkin' : 'capacity')
  }, [areAnytimeRates, offerPageDispatch, rooms, toggleFilter])

  const handleClearQuickFiltersClick = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.clearQuickFilters })
  }, [offerPageDispatch])

  const onChangeOccupancyClick = useCallback(() => {
    toggleFilter('capacity')
  }, [toggleFilter])

  const rateListId = useMemo(() => uuidV4(),
  // oxlint-disable-next-line react-hooks/exhaustive-deps
    [rates]) // regenerate id if packages change
  // Group property rates by refundability
  const groupedPropertyRates = useGroupedPropertyRatesByRefundability(rates)

  const upgradeCandidateByRateId = useMemo(() => {
    const candidatesByRateId = new Map<string, PropertyRateUpgradeCandidate>()
    rates.forEach(rate => {
      const candidate = getPropertyRateUpgradeCandidate([rate])
      if (candidate) {
        candidatesByRateId.set(rate.rateId, candidate)
      }
    })
    return candidatesByRateId
  }, [rates])
  const [showingMoreRates, setShowingMoreRates] = useState(false)
  const onToggleMoreRates = useCallback(() => {
    setShowingMoreRates(showing => !showing)
  }, [])

  const interestedInOptionText = intl.formatMessage({
    id: 'G-9nAaAqjseUDghjg-_K',
    defaultMessage: 'You were interested in this option.',
    description: 'Banner message when user previously viewed this specific rate option',
  })
  const interestedInRoomBookNowText = intl.formatMessage({
    id: 'aVl6CMk5kGcBFhZbG6_F',
    defaultMessage: 'You were interested in this room. Book now to secure your room!',
    description: 'Banner message encouraging user to book a previously viewed room',
  })
  const interestedInRoomText = intl.formatMessage({
    id: 'Dx8bErFB-i5-65LF4eFg',
    defaultMessage: 'You were interested in this room.',
    description: 'Banner message when user previously viewed this room',
  })
  const showMoreOptionsText = intl.formatMessage({
    id: 'k-g_5h0_vEmMsFKzMOYE',
    defaultMessage: 'Show more options',
    description: 'Button text to show more rate options in property room',
  })
  const showLessOptionsText = intl.formatMessage({
    id: 'BALA4ECaVVusLe3tdWN3',
    defaultMessage: 'Show less options',
    description: 'Button text to show fewer rate options in property room',
  })

  const carouselWidth = rates.length > 1 ? rem(300) : '100%'
  const roomSnapshotId = toOfferPageSnapshotId(roomId)
  const [roomScrollAnchor, setRoomScrollAnchor] = useState<HTMLDivElement | null>(null)

  return (
    <Pane data-testid="package-option">
      <ScrollAnchor ref={setRoomScrollAnchor} id={propertyRoom.id} vOffset={rem(80)} />
      {roomSnapshotId && <OfferPageFocusAnchor targetId={`package:${roomSnapshotId}`} anchorElement={roomScrollAnchor} />}

      {/* Selection indicator for multi-room */}
      {promoteRoom && (
        <MessageBanner kind="promotional" density="compact" flush="top" icon={<LineArrowDownIcon />}>
          {promoteRoomWithSpecificRate && <b>{interestedInOptionText}</b>}
          {promoteRoomWithSomeRates && <b>{interestedInRoomBookNowText}</b>}
          {promoteRoomWithoutRates && <b>{interestedInRoomText}</b>}
        </MessageBanner>
      )}

      <InView triggerOnce onChange={setRoomUrgencyInView}>
        <AccommodationSameRoomTile roomPackage={propertyRoom}>
          <AccommodationSameRoomTileImage roomPackage={propertyRoom} />
          <AccommodationSameRoomTileDetails
            roomPackage={propertyRoom}
            roomCount={roomCount}
            urgencyLabels={<>
              {bookingsCount && <BookingsCountLabel bookings={bookingsCount} threshold="room" />}
              {shouldShowSppRoomUrgency && canShowRatePriceRoomsRemainingLabel && <RoomsRemainingLabel
                // `canShowRatePriceRoomsRemainingLabel` being true already guarantees `availableRooms`
                // is a real number (checked inside `shouldShowRoomsRemainingLabelForVariant` above) --
                // `?? 0` is only to satisfy the type checker, this never actually applies at runtime.
                roomCount={availableRooms ?? 0}
                ratePrice={<FormatCurrency value={getPropertyRateDisplayPrice(firstRate)} currency={firstRate.currency} format="roundedDollar" />}
                copyVariant="rate-price"
                threshold="rate"
              />}
            </>}
            hideCounts={!!property.singleUnit}
          >
            <AccommodationRoomTileActions
              roomPackage={propertyRoom}
              state={actionsState}
              isWalled={property?.walledGarden}
              shouldShowAmenitiesAndPoliciesCta={false}
              onChangeOccupancyClick={onChangeOccupancyClick}
              onChangeDatesClick={handleChangeDatesClick}
              onCheckAvailabilityClick={handleCheckAvailabilityClick}
              onClearQuickFiltersClick={handleClearQuickFiltersClick}
            />
          </AccommodationSameRoomTileDetails>

          <AccommodationSameRoomTileOptions>
            {propertyRoom && areAnytimeRates && (
              <PropertyAnytimeRateOption propertyRoom={propertyRoom} />
            )}

            {/* Surface alternative available dates for single-unit properties when the selected
              dates are sold out */}
            {showRoomAvailabilityStrip && checkIn && (
              <PropertyRoomAvailabilityStrip
                propertyId={property.id}
                propertyRoomId={propertyRoom.id}
                propertyTimezone={property.location.timezone}
                checkIn={checkIn}
                duration={duration}
              />
            )}

            {/* Package carousel */}
            <CSSBreakpoint max="mobile" dynamic>
              <CardCarousel
                snap="start"
                width={carouselWidth}
                gap={12}
                gutterSize={20}
              >
                {take(rates, MAX_PROPERTY_ROOM_RATES_TO_SHOW).map(rate => {
                  const upgradeCandidate = upgradeCandidateByRateId.get(rate.rateId)

                  return <PropertyRateOption
                    key={rate.rateId}
                    lpcOffer={offer}
                    rate={rate}
                    roomListId={roomListId}
                    roomPosition={roomPosition}
                    rateListId={rateListId}
                    ratePosition={rate.defaultOrder + 1}
                    loyaltyUpgradeBanner={upgradeCandidate && <PropertyOfferPageLoyaltyStayUpgradeCallOutBanner
                      candidate={upgradeCandidate}
                      roomId={propertyRoom.id}
                      selectedRateId={rate.rateId}
                      propertyTimeZone={property.location.timezone}
                    />}
                  />
                })}
              </CardCarousel>
            </CSSBreakpoint>

            <CSSBreakpoint min="tablet" dynamic>
              {/* Property rates grouped by board code and inclusions */}
              <PropertyRoomDesktopPackages
                groupedPropertyRates={groupedPropertyRates}
                initialRatesToShow={initialRatesToShow}
                lpcOffer={offer}
                packageCount={groupedPropertyRates.length}
                propertyTimezone={property.location.timezone}
                rateListId={rateListId}
                roomId={propertyRoom.id}
                roomListId={roomListId}
                roomPosition={roomPosition}
                showLessOptionsText={showLessOptionsText}
                showMoreOptionsText={showMoreOptionsText}
                showingMoreRates={showingMoreRates}
                showMoreRates={onToggleMoreRates}
              />
            </CSSBreakpoint>
            {isLoading && <LoadingIndicator inline />}
          </AccommodationSameRoomTileOptions>
        </AccommodationSameRoomTile>
      </InView>
    </Pane>
  )
}

export default withOfferPageAndReduxState((state, appState) => {
  const mapping = getPropertyOfferMapping(state, appState)
  return {
    rooms: state.rooms,
    checkIn: state.checkInDate,
    checkOut: state.checkOutDate,
    hasLpcMapping: !!mapping?.lpcOfferIds?.length,
    availableForPurchase: isPropertyAvailableForPurchase(state, appState),
    areAnytimeRates: !getPropertyRatesKey(state, appState),
    isLoading: arePropertyRatesLoading(state, appState),
    duration: state.duration,
    open: !!state.activeFilter,
  }
}, PropertyRoomOption, preventRenderWithOpenFilter)
